import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { api, apiBaseUrl, tokenStore } from '@greenrinse/ui';

export interface NotificationDto {
  id: string; type: string; title: string; body: string; link: string | null; isRead: boolean; createdAtUtc: string; readAtUtc: string | null;
}

interface Page<T> { items: T[]; page: number; pageSize: number; total: number }

/**
 * Unread count + latest notifications for the bell, kept live over SignalR (/hubs/notifications) with a polling fallback.
 * One instance per app shell; pages that need the full list use /api/notifications directly.
 */
export function useNotifications(options: { pollMs?: number; onIncoming?: (n: NotificationDto) => void } = {}) {
  const { pollMs = 60_000, onIncoming } = options;
  const [unread, setUnread] = useState(0);
  const [latest, setLatest] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const incoming = useRef(onIncoming);
  incoming.current = onIncoming;

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [count, page] = await Promise.all([
        api<{ count: number }>('/api/notifications/unread-count'),
        api<Page<NotificationDto>>('/api/notifications', { query: { page: 1, pageSize: 15 } }),
      ]);
      setUnread(count.count);
      setLatest(page.items);
    } catch { /* keep last known state */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, pollMs);
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);

    const token = tokenStore.get()?.accessToken;
    let stop = () => undefined as void;
    if (token) {
      const conn = new HubConnectionBuilder()
        .withUrl(`${apiBaseUrl}/hubs/notifications`, { accessTokenFactory: () => tokenStore.get()?.accessToken ?? '' })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
      conn.on('notification', (n: NotificationDto) => {
        setLatest((l) => [n, ...l.filter((x) => x.id !== n.id)].slice(0, 15));
        setUnread((u) => u + 1);
        incoming.current?.(n);
      });
      conn.start().catch(() => { /* polling covers it */ });
      stop = () => { void conn.stop(); };
    }
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); stop(); };
  }, [refresh, pollMs]);

  const markRead = useCallback(async (id: string) => {
    setLatest((l) => l.map((n) => (n.id === id && !n.isRead ? { ...n, isRead: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    await api(`/api/notifications/${id}/read`, { method: 'POST' }).catch(() => undefined);
  }, []);

  const markAllRead = useCallback(async () => {
    setLatest((l) => l.map((n) => ({ ...n, isRead: true })));
    setUnread(0);
    await api('/api/notifications/read-all', { method: 'POST' }).catch(() => undefined);
  }, []);

  return { unread, latest, loading, refresh, markRead, markAllRead };
}

/** Relative time like "5m", "3h", "2d" — compact enough for the bell dropdown. */
export function timeAgo(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));
  if (s < 60) return 'now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}
